import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import {
  ApplicationPortfolioDetailPayload,
  LoanValidationDetailsType,
  PortfolioLoanStatusCode
} from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/api/types';
import { getPortfolioLoanStatusCodeColor } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/components/ApplicationOverviewDetails/utils/requestOverviewUtils';
import {
  getPortfolioLoanStatusCode,
  getPortfolioLoanStatusCodeName
} from 'Feature/V2/HbtPortfolioManagement/utils/loanStatusMapping';
import { TFunction } from 'i18next';
import { MasterPoolDetails, MasterPoolLoanComposition } from '../../HbtMasterPool/types';
import { TranslationType } from '../../HbtMasterPoolTable/types';
import { loanStatusMapping } from '../../HbtMasterPoolTable/utils/LoanStatusMapping';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import React from 'react';

export const getMasterPoolCmhcAccountNumber = (): string => {
  if (isServer()) return '';
  const params = new URLSearchParams(window.location.search);
  const masterPoolParams = params.get('masterPoolCmhcAccountNumber')?.split('?') ?? '';
  return masterPoolParams[0];
};
export const getMasterPoolCmhcLenderCode = (): string => {
  if (isServer()) return '';
  const params = new URLSearchParams(window.location.search);
  const masterPoolParamslenderCode = params.get('lenderCode');
  return masterPoolParamslenderCode ?? '';
};
export const getUrlParam = (param: string): string => {
  if (isServer()) return '';
  const params = new URLSearchParams(window.location.search);
  const paramValue = params.get(param);
  return paramValue ?? '';
};

export const getMasterPoolAccountStatusCode = (): string => {
  if (isServer()) return '';
  const params = new URLSearchParams(window.location.search);
  const masterPoolAccountStatusCode = params.get('masterPoolAccountStatusCode');
  return masterPoolAccountStatusCode ?? '';
};

export const getLoanStatusLabel = (status: keyof typeof loanStatusMapping, t: TranslationType) => {
  return loanStatusMapping(t)[status];
};

export const loansCompositionConverter = (
  masterPoolDetails: MasterPoolLoanComposition[],
  t: TranslationType
): LoanValidationDetailsType[] | undefined => {
  if (!Array.isArray(masterPoolDetails)) return [];

  const hasInvalidComposition = masterPoolDetails.some(
    (pool) => !Array.isArray(pool?.loanStatusCodeLevelComposition)
  );

  if (hasInvalidComposition) return undefined;

  return masterPoolDetails.flatMap((pool) => {
    const compositions = pool.loanStatusCodeLevelComposition!;

    return compositions.map((comp) => {
      const loanStatusCode = comp?.loanStatusCode ?? '';
      const loanStatusNameRaw = getPortfolioLoanStatusCodeName(String(loanStatusCode));

      const countPercentageStr = comp?.countPercentage ?? '0%';
      const amountPercentageStr = comp?.amountPercentage ?? '0%';

      const convertedPercentage = parseFloat(countPercentageStr.replace('%', '')) || 0;
      const convertedAmountPercentage = parseFloat(amountPercentageStr.replace('%', '')) || 0;

      return {
        amountPercentage: convertedAmountPercentage,
        countPercentage: convertedPercentage,
        totalAmount: Number(pool?.totalLoanAmountSubmitted) || 0,
        loanStatusName: getPortfolioLoanStatusCode(loanStatusNameRaw, t),
        color: getPortfolioLoanStatusCodeColor(loanStatusNameRaw),
        count: Number(comp?.totalLoanCount) || 0,
        amount: Number(comp?.amountSubmitted) || 0,
        loanStatusCode
      };
    });
  });
};

export const addTotalLoanCompositionRow = (
  loanCompositions: MasterPoolLoanComposition[],
  convertedDetails?: LoanValidationDetailsType[]
): LoanValidationDetailsType[] => {
  if (!loanCompositions?.length) return convertedDetails ?? [];

  const totalCount = loanCompositions[0]?.totalCount || 0;
  const totalAmount = loanCompositions[0]?.totalLoanAmountSubmitted || 0;

  const safeDetails = convertedDetails ?? [];

  const countPercentage = safeDetails.reduce(
    (acc, detail) => acc + Number(detail.countPercentage || 0),
    0
  );

  const amountPercentage = safeDetails.reduce(
    (acc, detail) => acc + Number(detail.amountPercentage || 0),
    0
  );

  const totalRow: LoanValidationDetailsType = {
    loanStatus: 'Total',
    count: totalCount,
    amount: totalAmount,
    countPercentage,
    amountPercentage,
    loanStatusName: 'Total'
  };

  return [...safeDetails, totalRow];
};

export const convertMasterPoolDetails = (
  masterPoolDetails: MasterPoolDetails[] | undefined
): ApplicationPortfolioDetailPayload | undefined => {
  if (!masterPoolDetails || masterPoolDetails.length === 0) return undefined;

  const data = masterPoolDetails[0];

  return {
    applicationRequestReceiptDate: data.requestReceiptDate.split('-').slice(0, 3).join('-'),
    applicationOfferRequestDate: data.offerRequestDate,
    insuredApprovedLenderCode: data.insuredLenderCode,
    insuredApprovedLenderName: {
      englishName: data.insuredLenderName?.englishName ?? '',
      frenchName: data.insuredLenderName?.frenchName ?? ''
    },
    portfolioNumberOfLoansSubmitted: data.totalSubmittedLoanCount,
    portfolioLastStatusUpdateTimestamp: data.finalizedDate,
    portfolioAmountSubmitted: data.totalSubmittedLoanAmount,
    portfolioOccupancyType: data.occupancyType,
    portfolioTermInsurance: data.termInsuranceYearCount,
    requestType: data.requestType
  };
};

export const getInsuranceDurationInYears = (
  pool: MasterPoolDetails,
  t: TFunction
): string | null => {
  const { termInsurance } = pool;

  const years = Number(termInsurance);
  if (!years || years < 1) return null;

  return `${years} ${t(years === 1 ? 'Year' : 'Years')}`;
};
export const hasValidInsuranceExpiryDate = (
  date?: string | null,
  termInsurance?: string
): boolean => {
  return !!termInsurance && !!date && !date.includes('9999-12-31');
};

export const checkForOfferDateRules = (accountStatusCode: string): boolean => {
  const VALID_MASTER_POOL_STATUS_CODES: PortfolioLoanStatusCode[] = [
    PortfolioLoanStatusCode.Cancelled,
    PortfolioLoanStatusCode.Rejected,
    PortfolioLoanStatusCode.Withdrawn,
    PortfolioLoanStatusCode.Approved,
    PortfolioLoanStatusCode.Finalized,
    PortfolioLoanStatusCode.Offer,
    PortfolioLoanStatusCode.Lock,
    PortfolioLoanStatusCode.Expired
  ];

  const isValidMasterPoolStatus = VALID_MASTER_POOL_STATUS_CODES.includes(
    accountStatusCode as PortfolioLoanStatusCode
  );

  return isValidMasterPoolStatus;
};

export const checkForWithdrawRules = (accountStatusCode: string): boolean => {
  const VALID_MASTER_POOL_STATUS_CODES: PortfolioLoanStatusCode[] = [
    PortfolioLoanStatusCode.Finalized,
    PortfolioLoanStatusCode.Withdrawn,
    PortfolioLoanStatusCode.Rerisk
  ];

  const isValidMasterPoolStatus = VALID_MASTER_POOL_STATUS_CODES.includes(
    accountStatusCode as PortfolioLoanStatusCode
  );

  return !isValidMasterPoolStatus;
};
export const checkForApprovalDateRules = (accountStatusCode: string): boolean => {
  const VALID_MASTER_POOL_STATUS_CODES: PortfolioLoanStatusCode[] = [
    PortfolioLoanStatusCode.Approved
  ];
  const isValidMasterPoolStatus = VALID_MASTER_POOL_STATUS_CODES.includes(
    accountStatusCode as PortfolioLoanStatusCode
  );

  return isValidMasterPoolStatus;
};

export const getRiskedOrOffered = (accountStatusCode: string): PortfolioLoanStatusCode | null => {
  const validStatuses = [PortfolioLoanStatusCode.Risked, PortfolioLoanStatusCode.Offer];

  return validStatuses.includes(accountStatusCode as PortfolioLoanStatusCode)
    ? (accountStatusCode as PortfolioLoanStatusCode)
    : null;
};
export const checkForReverseFinalizationRules = (accountStatusCode: string): boolean => {
  return [PortfolioLoanStatusCode.Finalized].includes(accountStatusCode as PortfolioLoanStatusCode);
};
export const checkForReverseWithdrawRules = (accountStatusCode: string): boolean => {
  return [PortfolioLoanStatusCode.Withdrawn].includes(accountStatusCode as PortfolioLoanStatusCode);
};

export const checkForApprovalRules = (accountStatusCode: string): boolean => {
  return [PortfolioLoanStatusCode.Lock].includes(accountStatusCode as PortfolioLoanStatusCode);
};

export const checkForFinalizeRules = (accountStatusCode: string): boolean => {
  return [PortfolioLoanStatusCode.Approved].includes(accountStatusCode as PortfolioLoanStatusCode);
};

export const getEmptyState = () => (
  <CmhcIcon size={CmhcIconSize.MEDIUM} type="icon_indeterminate" />
);
